/**
 * Service worker registration and web push subscription.
 *
 * The service worker itself is public/sw.js, plain JavaScript, hand written so
 * the push and notificationclick handlers are fully under our control. No
 * bundler magic and no build-time manifest, which also means the deployed file
 * is readable by whoever inherits this project.
 */

import { savePushSubscription } from './api'
import { detectPlatform, supportsPush } from './platform'
import { vapidPublicKey } from './supabase'

export const SW_URL = '/sw.js'

let registrationPromise: Promise<ServiceWorkerRegistration | null> | null = null

export function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return Promise.resolve(null)
  }
  if (!registrationPromise) {
    registrationPromise = navigator.serviceWorker
      .register(SW_URL, { scope: '/' })
      .catch((error: unknown) => {
        console.warn('Service worker registration failed', error)
        return null
      })
  }
  return registrationPromise
}

export function getRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return Promise.resolve(null)
  }
  return navigator.serviceWorker.ready.catch(() => null)
}

/** base64url VAPID key to the Uint8Array the Push API wants. */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const output = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i)
  return output
}

export type PushEnableResult =
  | { ok: true; endpoint: string }
  | { ok: false; reason: string }

/**
 * Must be called from a user gesture. Requests permission, creates the push
 * subscription, and stores it against the coach so the dispatcher Edge Function
 * can reach this device.
 */
export async function enablePush(coachId: string): Promise<PushEnableResult> {
  if (!supportsPush()) {
    return {
      ok: false,
      reason:
        'This browser cannot receive notifications. On iPhone, open the app from your home screen in Safari.',
    }
  }
  if (!vapidPublicKey) {
    return {
      ok: false,
      reason:
        'Push is not configured yet. VITE_VAPID_PUBLIC_KEY is missing from this build. Reminders will still appear in your inbox.',
    }
  }

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    return {
      ok: false,
      reason:
        permission === 'denied'
          ? 'Notifications are blocked. Turn them back on in your browser or phone settings, then try again.'
          : 'Notifications were not enabled. Tap the button and choose Allow.',
    }
  }

  const registration = await registerServiceWorker()
  if (!registration) {
    return { ok: false, reason: 'The background service could not start. Reload and try again.' }
  }
  await navigator.serviceWorker.ready

  let subscription = await registration.pushManager.getSubscription()
  if (!subscription) {
    try {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource,
      })
    } catch (error) {
      return {
        ok: false,
        reason: `Could not create a push subscription. ${(error as Error).message}`,
      }
    }
  }

  await savePushSubscription(coachId, subscription.toJSON(), detectPlatform())
  return { ok: true, endpoint: subscription.endpoint }
}

export async function currentPushEndpoint(): Promise<string | null> {
  const registration = await getRegistration()
  if (!registration) return null
  const subscription = await registration.pushManager.getSubscription()
  return subscription?.endpoint ?? null
}

/**
 * A local notification so a coach can confirm the plumbing works without
 * waiting for a real reminder. This does not exercise the server side, and the
 * onboarding copy says so.
 */
export async function showTestNotification(): Promise<boolean> {
  const registration = await getRegistration()
  if (!registration || Notification.permission !== 'granted') return false
  await registration.showNotification('Notifications are on', {
    body: 'This is what a La Folie reminder will look like.',
    icon: '/icons/icon-192.png',
    badge: '/icons/badge-72.png',
    tag: 'lafolie-test',
    data: { url: '/' },
  })
  return true
}
